import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import BedtimeStoryGenerator from './components/BedtimeStoryGenerator';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
// DISABLED: CartoonZPage, YPageCreator (features désactivées dans cette version)
import WelcomePage from './components/WelcomePage';
import BillingPage from './components/BillingPage';
import PaymentCallback from './components/PaymentCallback';
import PrivateAccess from './components/PrivateAccess';
import MyStoriesPage from './components/MyStoriesPage';
import { SplashCursor } from './components/SplashCursor';
import { AuthProvider } from './contexts/AuthContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { NavigationProvider, useNavigation } from './contexts/NavigationContext';
import AuthWrapper from './components/auth/AuthWrapper';
import { apiService } from './services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function App() {
  const [hasPrivateAccess, setHasPrivateAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier si l'accès privé est accordé
    const accessGranted = localStorage.getItem('private_access_granted');
    const accessTimestamp = localStorage.getItem('access_timestamp');
    
    if (accessGranted === 'true' && accessTimestamp) {
      const now = Date.now();
      const accessTime = parseInt(accessTimestamp);
      // L'accès expire après 24 heures
      const expirationTime = 24 * 60 * 60 * 1000;
      
      if (now - accessTime < expirationTime) {
        setHasPrivateAccess(true);
      } else {
        // Accès expiré, nettoyer le localStorage
        localStorage.removeItem('private_access_granted');
        localStorage.removeItem('access_timestamp');
      }
    }
    
    setLoading(false);
  }, []);

  const handleAccessGranted = () => {
    setHasPrivateAccess(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Private access disabled - direct access to app
  // if (!hasPrivateAccess) {
  //   return <PrivateAccess onAccessGranted={handleAccessGranted} />;
  // }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <PaymentProvider>
          <LanguageProvider>
            <NavigationProvider>
              <Router>
                <Routes>
                  {/* Payment callback routes - outside AuthWrapper */}
                  <Route path="/payment/success" element={<PaymentCallback />} />
                  <Route path="/payment/cancelled" element={<PaymentCallback />} />
                  <Route path="/payment/callback" element={<PaymentCallback />} />
                  
                  {/* Main app routes - inside AuthWrapper */}
                  <Route path="/*" element={
                    <AuthWrapper>
                      <MagicZApp />
                    </AuthWrapper>
                  } />
                </Routes>
              </Router>
            </NavigationProvider>
          </LanguageProvider>
        </PaymentProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

function MagicZApp() {
  const { language, setLanguage, languages } = useLanguage();
  const { saveTabState, getTabState, tabStates } = useNavigation();
  const [sessionId] = useState(() => 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  const [activeZSection, setActiveZSection] = useState('stories'); // 'stories', 'music', 'games'
  
  // Initialize messages based on active section
  const getInitialMessages = (section) => {
    switch(section) {
      case 'music':
        return [{
          role: 'bot',
          content: '🎵 Hello! I\'m your magical assistant for creating personalized music for your child! ✨\n\n**Step 1**: Tell me your child\'s name to create a special song just for them! 🎶',
          step: 1
        }];
      case 'games':
        return [{
          role: 'bot', 
          content: '🎮 Hello! I\'m your magical assistant for creating educational games for your child! ✨\n\n**Step 1**: Tell me your child\'s name to create a special game just for them! 🎯',
          step: 1
        }];
      default:
        return [{
          role: 'bot',
          content: '👋 Hello! I\'m your magical assistant for creating personalized bedtime stories! ✨\n\n**Step 1**: Can you upload a photo of your child? I\'ll transform it into a magical cartoon character! 📸',
          step: 1,
          hasUploadButton: true
        }];
    }
  };
  
  const [messages, setMessages] = useState(() => getInitialMessages('stories'));
  const [generatedContent, setGeneratedContent] = useState(null); // For music, games, or stories
  const [currentStep, setCurrentStep] = useState(1);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionData, setSessionData] = useState({});
  const sessionDataRef = useRef({}); // Ref for synchronous access to session data
  const [story, setStory] = useState(null);
  const [showStory, setShowStory] = useState(false);
  const [currentStoryPart, setCurrentStoryPart] = useState(0);
  const [pendingData, setPendingData] = useState(null);
  const [waitingForValidation, setWaitingForValidation] = useState(false);
  const [animatingStep, setAnimatingStep] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Try to restore the last active tab from navigation state
    const lastVisitedTab = Object.keys(tabStates).reduce((latest, tabName) => {
      const tabState = tabStates[tabName];
      if (!latest || (tabState.lastVisited && tabState.lastVisited > latest.lastVisited)) {
        return { tab: tabName, lastVisited: tabState.lastVisited };
      }
      return latest;
    }, null);
    
    const allowedTabs = ['WhizZ', 'MyStories', 'Profile'];
    return lastVisitedTab && allowedTabs.includes(lastVisitedTab.tab) ? lastVisitedTab.tab : 'WhizZ';
  });
  const [hasNewStory, setHasNewStory] = useState(false); // State for story notification
  // Reset chat when changing Z section
  const handleZSectionChange = (newSection) => {
    setActiveZSection(newSection);
    setMessages(getInitialMessages(newSection));
    setCurrentStep(1);
    setSessionData({});
    sessionDataRef.current = {}; // Reset ref when changing section
    setGeneratedContent(null);
    setShowStory(false);
    setWaitingForValidation(false);
    setAnimatingStep(null);
  };

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Function to handle tab switching with state persistence
  const handleTabSwitch = (newTab) => {
    // Save current tab state before switching
    if (activeTab === 'WhizZ') {
      // Save Z chat state (this is mostly for the old Z functionality)
      saveTabState('Z', {
        messages,
        currentStep,
        sessionData,
        story,
        showStory,
        currentStoryPart,
        pendingData,
        waitingForValidation,
        animatingStep
      });
    }
    
    // Clear notification badge when visiting My Stories
    if (newTab === 'MyStories') {
      setHasNewStory(false);
    }
    
    // Set the new active tab
    setActiveTab(newTab);
    
    // Mark this tab as visited
    saveTabState(newTab, {});
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageData = e.target.result;
        setIsLoading(true);
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: 'Photo uploaded',
              step: currentStep,
              session_id: sessionId,
              image_data: imageData
            })
          });

          const data = await response.json();
          
          if (data.success) {
            setMessages(prev => {
              const messages = [
                ...prev, 
                { role: 'user', content: '📸 Photo uploaded!', step: currentStep }
              ];
              
              const updatedMessages = messages.map(msg => {
                if (msg.hasUploadButton) {
                  return { ...msg, hasUploadButton: false };
                }
                return msg;
              });
              
              if (data.session_data?.child_cartoon) {
                updatedMessages.push({ 
                  role: 'bot', 
                  content: data.response,
                  step: data.step, 
                  cartoonImage: data.session_data.child_cartoon,
                  isResultMessage: true 
                });
              } else {
                updatedMessages.push({ 
                  role: 'bot', 
                  content: data.response, 
                  step: data.step,
                  isResultMessage: true 
                });
              }
              
              return updatedMessages;
            });
            
            setPendingData({
              ...data.session_data,
              nextQuestion: data.session_data?.next_question,
              nextStep: data.step
            });
            setWaitingForValidation(true);
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          setMessages(prev => [...prev, { 
            role: 'bot', 
            content: 'Sorry, there was an issue with the upload. Can you try again?',
            step: currentStep 
          }]);
        }
        
        setIsLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const message = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    setMessages(prev => [...prev, { role: 'user', content: message, step: currentStep }]);

    try {
      // Handle different content types
      if (activeZSection === 'music') {
        await handleMusicMessage(message);
      } else if (activeZSection === 'games') {
        await handleGameMessage(message);
      } else {
        await handleStoryMessage(message);
      }
    } catch (error) {
      console.error('Message error:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'Sorry, something went wrong. Please try again.', 
        step: currentStep 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMusicMessage = async (message) => {
    let responseText = "";
    
    if (currentStep === 1) {
      // Step 1: Child name
      sessionDataRef.current = { ...sessionDataRef.current, child_name: message };
      setSessionData(sessionDataRef.current);
      responseText = `Perfect! ${message} is a beautiful name! 🌟`;
      setCurrentStep(2);
      setPendingData({ nextQuestion: "**Step 2**: How old is **" + message + "**? 🎂", nextStep: 2 });
    } else if (currentStep === 2) {
      // Step 2: Age
      sessionDataRef.current = { ...sessionDataRef.current, age: message };
      setSessionData(sessionDataRef.current);
      responseText = `Great! ${message} years old - perfect for making music! 🎵`;
      setCurrentStep(3);
      setPendingData({ nextQuestion: "**Step 3**: What are **their interests**? \n\nFor example: dancing 💃, animals 🦁, nature 🌳, sports ⚽, art 🎨...\n\nTell me what they love!", nextStep: 3 });
    } else if (currentStep === 3) {
      // Step 3: Interests
      sessionDataRef.current = { ...sessionDataRef.current, interests: message };
      setSessionData(sessionDataRef.current);
      responseText = `Wonderful! These interests will make the song special! ✨`;
      setCurrentStep(4);
      setPendingData({ nextQuestion: "**Step 4**: What **musical style** would you like?", nextStep: 4 });
    } else if (currentStep === 4) {
      // Step 4: Musical style
      sessionDataRef.current = { ...sessionDataRef.current, musical_style: message };
      setSessionData(sessionDataRef.current);
      responseText = `Excellent choice! This style will be perfect! ✨`;
      setCurrentStep(5);
      setPendingData({ nextQuestion: "**Step 5**: What **positive message** do you want to convey in this song?\n\nFor example:\n• \"Be confident in yourself\" 💪\n• \"The importance of friendship\" 👫\n• \"Be kind and caring\" 💕\n• \"Follow your dreams\" 🌟\n\nWhat message do you want to share?", nextStep: 5 });
    } else if (currentStep === 5) {
      // Step 5: Message/lesson
      sessionDataRef.current = { ...sessionDataRef.current, message: message };
      setSessionData(sessionDataRef.current);
      responseText = `Beautiful! This message will make the song even more special! ✨`;
      setCurrentStep(6);
      setPendingData({ ...sessionDataRef.current, nextQuestion: "**Final step**: Ready to create your magical song?\n\n🎵 Your personalized song will be crafted with beautiful lyrics and melodies just for your child!", nextStep: 6, hasMusicButton: true });
    }

    setMessages(prev => [...prev, { role: 'bot', content: responseText, step: currentStep }]);
    setWaitingForValidation(true);
  };

  const handleGameMessage = async (message) => {
    if (currentStep === 1) {
      // Step 1: Child name  
      setSessionData(prev => ({ ...prev, child_name: message }));
      
      setMessages(prev => [
        ...prev, 
        { role: 'bot', content: `Fantastic! ${message} will love their personalized game! 🌟`, step: 1 },
        { role: 'bot', content: `**Step 2**: Choisissez la tranche d'âge de **${message}** ?`, step: 2 }
      ]);
      
      setCurrentStep(2);
    }
  };

  const selectAgeRange = async (ageRange) => {
    setSessionData(prev => ({ ...prev, age_range: ageRange }));
    
    setMessages(prev => [...prev, { 
      role: 'user', 
      content: `Tranche d'âge sélectionnée: ${ageRange} ans`, 
      step: currentStep 
    }]);
    
    setWaitingForValidation(false);
    await generateGame(ageRange);
  };

  const generateGame = async (ageRange) => {
    try {
      setIsLoading(true);
      
      const response = await apiService.z.generateGame({
        child_name: sessionData.child_name,
        age_range: ageRange
      });

      const data = response.data;
      
      if (data.success) {
        setGeneratedContent(data.game);
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: `🎉 Le jeu éducatif pour ${sessionData.child_name} est prêt! Vous pouvez le voir dans la prévisualisation. ✨`,
          step: 3,
          showGameButton: true
        }]);
        setCurrentStep(3);
      } else {
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: `Désolé, il y a eu un problème lors de la génération du jeu: ${data.error || 'Erreur inconnue'}. Veuillez réessayer.`,
          step: 2
        }]);
      }
    } catch (error) {
      console.error('Game generation error:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'Désolé, quelque chose s\'est mal passé lors de la génération du jeu. Veuillez réessayer.',
        step: 2
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoryMessage = async (message) => {
    try {
      const response = await apiService.z.sendMessage({
        message: message,
        step: currentStep,
        session_id: sessionId
      });

      const data = response.data;
      
      if (data.success) {
        setMessages(prev => {
          const messages = [...prev, { role: 'bot', content: data.response, step: data.step }];
          
          if (currentStep === 4) {
            return messages.map(msg => {
              if (msg.hasToneButtons) {
                return { ...msg, hasToneButtons: false };
              }
              return msg;
            });
          }
          return messages;
        });
        
        setSessionData(data.session_data);
        setCurrentStep(data.step);
        setWaitingForValidation(true);
        setPendingData(data.session_data);
      }
    } catch (error) {
      console.error('Story message error:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'Sorry, something went wrong. Please try again.',
        step: currentStep 
      }]);
    }
  };
  const generateMusic = async () => {
    try {
      setIsLoading(true);
      
      // Use sessionDataRef for synchronous access to all data
      const musicData = {
        child_name: sessionDataRef.current.child_name,
        age: sessionDataRef.current.age,
        interests: sessionDataRef.current.interests,
        musical_style: sessionDataRef.current.musical_style,
        message: sessionDataRef.current.message
      };
      
      // Debug log
      console.log('🎵 Generating music with data:', musicData);
      
      // Validate all fields before sending
      const missingFields = Object.entries(musicData)
        .filter(([key, value]) => !value)
        .map(([key]) => key);
      
      if (missingFields.length > 0) {
        console.error('Missing fields:', missingFields);
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: `Please complete all steps before generating. Missing: ${missingFields.join(', ')}`,
          step: 6
        }]);
        return;
      }
      
      const response = await apiService.z.generateMusic(musicData);

      const data = response.data;
      
      if (data.success) {
        setGeneratedContent(data.music);
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: `🎉 Your personalized song for ${sessionData.child_name} is ready! You can listen to it in the preview panel. ✨`,
          step: 7,
          showMusicButton: true
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: `Sorry, there was an issue generating the music: ${data.error || 'Unknown error'}. Please try again.`,
          step: 6
        }]);
      }
    } catch (error) {
      console.error('Music generation error:', error);
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: 'Sorry, something went wrong while generating the music. Please try again.',
        step: 6
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep = () => {
    if (pendingData) {
      setAnimatingStep(currentStep);
      
      setSessionData(prev => ({ ...prev, ...pendingData }));
      
      setMessages(prev => [...prev, { 
        role: 'bot', 
        content: '✅ Perfect! Let\'s move to the next step!',
        step: currentStep,
        validated: true
      }]);
      
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setAnimatingStep(null);
      }, 800);
      
      if (pendingData.nextQuestion) {
        setTimeout(() => {
          const messageData = { 
            role: 'bot', 
            content: pendingData.nextQuestion, 
            step: pendingData.nextStep,
            isQuestionMessage: true 
          };
          
          if (pendingData.nextStep === 4) {
            messageData.hasToneButtons = true;
          }
          
          if (pendingData.nextStep === 6 && activeZSection === 'music') {
            messageData.hasMusicButton = true;
          }
          
          setMessages(prev => [...prev, messageData]);
        }, 1300);
      }
      
      setPendingData(null);
      setWaitingForValidation(false);
    }
  };

  const editStep = () => {
    setPendingData(null);
    setWaitingForValidation(false);
    
    setMessages(prev => [...prev, { 
      role: 'bot', 
      content: '🔄 No problem! You can modify this step.',
      step: currentStep
    }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getStepButtons = () => {
    if (currentStep === 1) {
      return null;
    }

    if (currentStep === 2 && activeZSection === 'games') {
      return (
        <div className="flex flex-col gap-3">
          <button
            onClick={() => selectAgeRange('3-4')}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 transform hover:scale-105 text-lg"
            disabled={isLoading}
          >
            🧒 3-4 ans
            <div className="text-sm opacity-90 mt-1">Jeux adaptés aux tout-petits</div>
          </button>
          <button
            onClick={() => selectAgeRange('5-7')}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 transform hover:scale-105 text-lg"
            disabled={isLoading}
          >
            👶 5-7 ans
            <div className="text-sm opacity-90 mt-1">Jeux éducatifs pour enfants</div>
          </button>
          <button
            onClick={() => selectAgeRange('8-10')}
            className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white px-8 py-4 rounded-xl font-bold transition-all duration-200 transform hover:scale-105 text-lg"
            disabled={isLoading}
          >
            🧑 8-10 ans
            <div className="text-sm opacity-90 mt-1">Jeux plus complexes</div>
          </button>
        </div>
      );
    }

    if (currentStep === 4) {
      return null;
    }

    return null;
  };

  const formatMessage = (content) => {
    return content.split('\n').map((line, index) => {
      if (line.includes('**') && line.includes('**')) {
        const parts = line.split('**');
        return (
          <div key={index} className="mb-2">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-purple-800">{part}</strong> : part
            )}
          </div>
        );
      }
      return <div key={index} className="mb-2">{line}</div>;
    });
  };

  const renderTabContent = () => {
    return (
      <>
        <div style={{ display: activeTab === 'WhizZ' ? 'block' : 'none' }}>
          <BedtimeStoryGenerator 
            onStorySaved={() => setHasNewStory(true)}
          />
        </div>
        <div style={{ display: activeTab === 'Profile' ? 'block' : 'none' }}>
          <ProfilePage />
        </div>
        <div style={{ display: activeTab === 'MyStories' ? 'block' : 'none' }}>
          <MyStoriesPage setActiveTab={handleTabSwitch} isActive={activeTab === 'MyStories'} />
        </div>
      </>
    );
  };

  const renderMagicZContent = () => {
    if (showStory && story) {
      const totalParts = story.story_parts?.length || 0;
      
      const nextPart = () => {
        if (currentStoryPart < totalParts - 1) {
          setCurrentStoryPart(prev => prev + 1);
        }
      };
      
      const prevPart = () => {
        if (currentStoryPart > 0) {
          setCurrentStoryPart(prev => prev - 1);
        }
      };
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-8">
              <button
                onClick={() => setShowStory(false)}
                className="mb-4 bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-full transition-colors"
              >
                ← Back to chat
              </button>
              <h1 className="text-4xl font-bold text-purple-800 mb-2">🌟 Your Magical Story 🌟</h1>
              <p className="text-lg text-gray-600">A personalized adventure for {sessionData.age} years old</p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="w-full">
                  {story.visuals?.[currentStoryPart] && (
                    <div className="w-full">
                      {story.visuals[currentStoryPart].type === 'image' ? (
                        story.visuals[currentStoryPart].url ? (
                          <img 
                            src={story.visuals[currentStoryPart].url}
                            alt={`Scene ${currentStoryPart + 1}`}
                            className="w-full h-64 md:h-80 object-cover"
                          />
                        ) : (
                          <div className="w-full h-64 md:h-80 bg-gray-200 flex items-center justify-center text-gray-500">
                            🎨 Image being created...
                          </div>
                        )
                      ) : (
                        story.visuals[currentStoryPart].url ? (
                          <video 
                            src={story.visuals[currentStoryPart].url}
                            controls
                            className="w-full h-64 md:h-80 object-cover"
                            poster={story.visuals[currentStoryPart].thumbnail}
                          >
                            Your browser doesn't support videos.
                          </video>
                        ) : (
                          <div className="w-full h-64 md:h-80 bg-gray-200 flex items-center justify-center text-gray-500">
                            🎬 Video being created...
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                
                <div className="p-8">
                  <div className="min-h-[200px] flex items-center justify-center">
                    <div className="text-center max-w-3xl">
                      <p className="text-xl leading-relaxed text-gray-700 font-medium">
                        {story.story_parts?.[currentStoryPart] || 'Story loading...'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center mt-8">
                    <button
                      onClick={prevPart}
                      disabled={currentStoryPart === 0}
                      className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                        currentStoryPart === 0 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white transform hover:scale-105'
                      }`}
                    >
                      ← Previous
                    </button>
                    
                    <div className="flex space-x-2">
                      {Array.from({length: totalParts}, (_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentStoryPart(index)}
                          className={`w-3 h-3 rounded-full transition-all duration-200 ${
                            index === currentStoryPart ? 'bg-purple-500 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <button
                      onClick={nextPart}
                      disabled={currentStoryPart === totalParts - 1}
                      className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                        currentStoryPart === totalParts - 1 
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white transform hover:scale-105'
                      }`}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-12">
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-purple-800 mb-4">🌙 Good night! 🌙</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    I hope you enjoyed this story! Sweet dreams! ✨
                  </p>
                  <button
                    onClick={() => {
                      setShowStory(false);
                      setCurrentStoryPart(0);
                      setMessages([{
                        role: 'bot',
                        content: '👋 Do you want to create a new magical story? Upload a new photo to start! 📸',
                        step: 1
                      }]);
                      setCurrentStep(1);
                      setSessionData({});
                      setStory(null);
                    }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-3 rounded-full font-bold transition-all duration-200 transform hover:scale-105"
                  >
                    ✨ Create a new story
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Main Z page with tabs - modern interface
    return (
      <div className="h-full bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 flex flex-col">
        {/* Z Header with Navigation Tabs - Fixed at top */}
        <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="max-w-6xl mx-auto px-4 py-4 md:py-6">
            <div className="text-center mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center justify-center gap-3">
                <svg className="w-6 h-6 md:w-8 md:h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Z Creator Studio
              </h1>
              <p className="text-gray-600 mt-1 text-sm md:text-base">Your AI-powered creative assistant</p>
            </div>
            
            {/* Modern Tabs */}
            <div className="flex justify-center">
              <div className="bg-gray-100 rounded-2xl p-2 flex gap-2">
                <button
                  onClick={() => handleZSectionChange('stories')}
                  className={`px-4 md:px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                    activeZSection === 'stories'
                      ? 'bg-white text-purple-600 shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                  Stories
                </button>
                
                <button
                  onClick={() => handleZSectionChange('music')}
                  className={`px-4 md:px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                    activeZSection === 'music'
                      ? 'bg-white text-indigo-600 shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  Music
                </button>
                
                <button
                  onClick={() => handleZSectionChange('games')}
                  className={`px-4 md:px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                    activeZSection === 'games'
                      ? 'bg-white text-green-600 shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v3a3 3 0 003 3z" />
                  </svg>
                  Games
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area - Scrollable below header */}
        <div className="flex-1 stars-background flex flex-col overflow-hidden">
          <div className="relative z-10 h-full flex flex-col">
            {/* Steps - Fixed position with high z-index */}
            <div className="flex justify-center items-center gap-3 py-4 mt-8 md:mt-12 flex-shrink-0 relative z-40">
              {[1, 2, 3, 4, 5, 6].map((stepNumber) => {
                const isCompleted = stepNumber < currentStep;
                const isCurrent = stepNumber === currentStep;
                const isPending = stepNumber > currentStep;
                const isAnimating = animatingStep === stepNumber;
                
                return (
                  <div 
                    key={stepNumber}
                    className={`relative w-10 h-10 rounded-full transition-all duration-700 ease-in-out transform ${
                      isCompleted 
                        ? 'bg-gradient-to-r from-green-400 to-emerald-500 scale-100 shadow-lg step-complete' 
                        : isCurrent 
                          ? `bg-gradient-to-r from-purple-500 to-pink-500 scale-125 shadow-xl glow-pulse ${isAnimating ? 'step-activate' : ''}` 
                          : 'bg-gray-300 scale-90 opacity-60'
                    }`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      {isCompleted ? (
                        <svg 
                          className="w-5 h-5 text-white checkmark" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                          strokeDasharray="50"
                          strokeDashoffset="50"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth="3" 
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <span className={`text-sm font-bold transition-colors duration-300 ${
                          isCurrent ? 'text-white' : isPending ? 'text-gray-500' : 'text-white'
                        }`}>
                          {stepNumber}
                        </span>
                      )}
                    </div>
                    
                    {stepNumber < 6 && (
                      <div className={`absolute top-1/2 left-full w-3 h-0.5 transform -translate-y-1/2 transition-all duration-700 ease-in-out ${
                        stepNumber < currentStep 
                          ? 'bg-gradient-to-r from-green-400 to-emerald-500 progress-fill' 
                          : 'bg-gray-300'
                      }`}></div>
                    )}
                    
                    {isCurrent && (
                      <>
                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-ping opacity-20"></div>
                        <div className="absolute -inset-2 rounded-full border-2 border-purple-400 animate-pulse opacity-50"></div>
                      </>
                    )}
                    
                    {isAnimating && (
                      <div className="absolute -inset-4 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 animate-ping opacity-30"></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Two cards side by side - Full height container */}
            <div className="flex gap-4 flex-1 min-h-0 px-4 pb-8 md:pb-12">
              {/* Chat container card - left side */}
              <div className="flex-1 min-h-0">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl h-full flex flex-col">
                  {/* Chat messages area with fixed height and scroll */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                  {messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-2xl ${
                        message.role === 'user' 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                          : message.isResultMessage
                            ? 'bg-gradient-to-r from-green-100 to-blue-100 text-gray-800 border-2 border-green-200'
                            : message.isQuestionMessage
                              ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-gray-800 border-2 border-yellow-200'
                              : 'bg-gray-100 text-gray-800'
                      }`}>
                        <div className="text-sm leading-relaxed">
                          {formatMessage(message.content)}
                        </div>
                        
                        {message.cartoonImage && (
                          <div className="mt-2 text-center">
                            <div className="bg-white p-2 rounded-xl shadow-lg border-2 border-purple-200">
                              <img 
                                src={message.cartoonImage}
                                alt="Personnage cartoon généré"
                                className="w-32 h-32 object-cover rounded-lg mx-auto shadow-md"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <p className="text-xs text-purple-600 mt-1 font-bold">
                                🎨 Your magical character!
                              </p>
                            </div>
                          </div>
                        )}

                        {message.hasUploadButton && (
                          <div className="mt-3 text-center">
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-full font-medium transition-all duration-200 transform hover:scale-105 shadow-lg text-sm"
                              disabled={isLoading}
                            >
                              📸 Upload Photo
                            </button>
                          </div>
                        )}

                        {message.hasToneButtons && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            {[
                              { text: 'Funny and silly', value: 'funny silly' },
                              { text: 'Adventurous', value: 'adventurous' },
                              { text: 'Gentle and comforting', value: 'gentle comforting' },
                              { text: 'Fantasy and magical', value: 'fantasy magical' }
                            ].map((tone, index) => (
                              <button
                                key={index}
                                onClick={() => {
                                  setInputMessage(tone.value);
                                  setTimeout(sendMessage, 100);
                                }}
                                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-3 py-2 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 text-xs"
                                disabled={isLoading}
                              >
                                {tone.text}
                              </button>
                            ))}
                          </div>
                        )}
                  
                        {message.hasMusicButton && (
                          <div className="mt-4 flex justify-center">
                            <button
                              onClick={() => {
                                generateMusic();
                                setMessages(prev => prev.map(msg => msg.hasMusicButton ? {...msg, hasMusicButton: false} : msg));
                              }}
                              className="group relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-indigo-500/25 border border-white/20"
                              disabled={isLoading}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                              <div className="relative flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12c0-2.21-.895-4.21-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 12a5.983 5.983 0 01-.757 2.829 1 1 0 01-1.415-1.414A3.987 3.987 0 0013 12a3.987 3.987 0 00-.172-1.415 1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="text-left">
                                  <div className="text-lg font-extrabold tracking-wide">🎵 Generate My Song</div>
                                  <div className="text-xs opacity-90 font-medium">Create magical music for {sessionData.child_name}</div>
                                </div>
                                <div className="ml-2">
                                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                </div>
                              </div>
                            </button>
                          </div>
                        )}

                        {message.showStoryButton && (
                          <button
                            onClick={() => setShowStory(true)}
                            className="mt-2 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white px-4 py-2 rounded-full font-bold transition-all duration-200 transform hover:scale-105 w-full text-sm"
                          >
                            🎭 Discover my story!
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 px-3 py-2 rounded-2xl">
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-purple-500 border-t-transparent"></div>
                          <span className="text-gray-600 text-sm">Magical creation in progress...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {waitingForValidation && !isLoading && (
                    <div className="flex justify-start">
                      <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-2xl bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-200">
                        <div className="text-sm leading-relaxed">
                          <div className="text-center text-gray-700 mb-2 font-medium">
                            🤔 Are you satisfied with this step?
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={validateStep}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-3 py-1.5 rounded-full font-medium transition-all duration-200 transform hover:scale-105 text-xs"
                            >
                              ✅ Validate and continue
                            </button>
                            <button
                              onClick={editStep}
                              className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white px-3 py-1.5 rounded-full font-medium transition-all duration-200 transform hover:scale-105 text-xs"
                            >
                              ✏️ Modify
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Fixed input area at bottom - never changes */}
                <div className="border-t border-gray-200 p-3 bg-white/90 flex-shrink-0 rounded-b-3xl">
                  {!waitingForValidation && (
                    <>
                      <div className="mb-2">
                        {getStepButtons()}
                      </div>

                      {(currentStep === 2 || currentStep === 3 || currentStep === 5 || 
                        (currentStep === 1 && (activeZSection === 'music' || activeZSection === 'games')) ||
                        (currentStep === 2 && activeZSection !== 'games')) && (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={
                              currentStep === 1 && activeZSection === 'music' ? "Enter your child's name..." :
                              currentStep === 1 && activeZSection === 'games' ? "Enter your child's name..." :
                              currentStep === 2 ? "Enter your child's age..." :
                              currentStep === 3 ? "Describe their interests..." :
                              "What message do you want to convey?"
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                            disabled={isLoading}
                          />
                          <button
                            onClick={sendMessage}
                            disabled={!inputMessage.trim() || isLoading}
                            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-full font-medium transition-all duration-200 transform hover:scale-105"
                          >
                            ✨
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              </div>
              
              {/* Content preview card - right side with dynamic content */}
              <div className="flex-1 min-h-0">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="text-center text-gray-500">
                      {activeZSection === 'stories' && (
                        <>
                          <h3 className="text-lg font-bold mb-4">📖 Story Preview</h3>
                          <div className="bg-gray-100 rounded-xl p-8 h-64 flex items-center justify-center">
                            <p className="text-gray-400">Your story will appear here</p>
                          </div>
                        </>
                      )}
                      {activeZSection === 'music' && (
                        <>
                          <h3 className="text-lg font-bold mb-4">🎵 Music Preview</h3>
                          {generatedContent ? (
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-6 h-auto">
                              <div className="text-center mb-4">
                                <h4 className="text-xl font-bold text-indigo-800 mb-2">
                                  {generatedContent.content?.title || `Song for ${sessionData.child_name}`}
                                </h4>
                                <p className="text-indigo-600 text-sm">
                                  Personalized for {sessionData.child_name} ({sessionData.age} years old)
                                </p>
                              </div>
                              
                              {/* Audio Player */}
                              {generatedContent.content?.audio_url && (
                                <div className="mb-4">
                                  <audio 
                                    controls 
                                    className="w-full"
                                    src={generatedContent.content.audio_url}
                                  >
                                    Your browser does not support the audio element.
                                  </audio>
                                </div>
                              )}
                              
                              {/* Lyrics Display */}
                              {generatedContent.content?.lyrics && (
                                <div className="bg-white/80 rounded-lg p-4 text-left">
                                  <h5 className="font-semibold text-indigo-700 mb-2">🎤 Lyrics:</h5>
                                  <div className="text-gray-700 text-sm whitespace-pre-line">
                                    {generatedContent.content.lyrics}
                                  </div>
                                </div>
                              )}
                              
                              {/* Music Info */}
                              <div className="mt-4 flex items-center justify-between text-xs text-indigo-600">
                                <span>Style: {sessionData.musical_style}</span>
                                {generatedContent.content?.duration && (
                                  <span>Duration: {Math.floor(generatedContent.content.duration / 60)}:{(generatedContent.content.duration % 60).toString().padStart(2, '0')}</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl p-8 h-64 flex items-center justify-center">
                              <div className="text-center">
                                <svg className="w-16 h-16 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                </svg>
                                <p className="text-gray-400">Your personalized music will appear here</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {activeZSection === 'games' && (
                        <>
                          <h3 className="text-lg font-bold mb-4">🎮 Game Preview</h3>
                          {generatedContent ? (
                            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6 h-auto">
                              <div className="text-center mb-4">
                                <h4 className="text-xl font-bold text-green-800 mb-2">
                                  {sessionData.child_name}
                                </h4>
                                <p className="text-green-600 text-sm">
                                  Jeu éducatif pour {sessionData.age_range} ans
                                </p>
                              </div>
                              
                              {/* Game Content */}
                              <div className="bg-white rounded-lg p-4 h-96 overflow-auto">
                                <div 
                                  dangerouslySetInnerHTML={{
                                    __html: generatedContent.content?.game_html || generatedContent.game_html
                                  }}
                                />
                              </div>
                              
                              {/* Game Info */}
                              <div className="mt-4 text-xs text-green-600 text-center">
                                <span>Jeu interactif personnalisé pour {sessionData.child_name}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-8 h-64 flex items-center justify-center">
                              <div className="text-center">
                                <svg className="w-16 h-16 mx-auto mb-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v3a3 3 0 003 3z" />
                                </svg>
                                <p className="text-gray-400">Your educational game will appear here</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-[100dvh] bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 flex flex-col">
      {/* Header Navigation - Always visible */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 flex-shrink-0 relative z-20">
        <div className="container mx-auto px-4 py-3">
          <div className="h-2"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {renderTabContent()}
      </main>

      {/* Footer Navigation */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-gray-200 flex-shrink-0">
        <div className="container mx-auto px-4 py-2">
          <div className="flex justify-center items-center space-x-6">
            {/* WhizZ Tab */}
            <button
              onClick={() => handleTabSwitch('WhizZ')}
              className={`flex flex-col items-center space-y-1 px-3 py-1 rounded-full transition-all duration-200 ${
                activeTab === 'WhizZ' 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                activeTab === 'WhizZ' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xs font-medium">WhizZ</span>
            </button>

            {/* Stories Tab */}
            <button
              onClick={() => handleTabSwitch('MyStories')}
              className={`flex flex-col items-center space-y-1 px-3 py-1 rounded-full transition-all duration-200 ${
                activeTab === 'MyStories' 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                activeTab === 'MyStories' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                
                {/* Notification Badge */}
                {hasNewStory && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                )}
              </div>
              <span className="text-xs font-medium">Stories</span>
            </button>

            {/* Profile Tab */}
            <button
              onClick={() => handleTabSwitch('Profile')}
              className={`flex flex-col items-center space-y-1 px-3 py-1 rounded-full transition-all duration-200 ${
                activeTab === 'Profile' 
                  ? 'bg-purple-100 text-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                activeTab === 'Profile' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="text-xs font-medium">Profile</span>
            </button>

          </div>
        </div>
      </footer>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
}

export default App;